/**
 * Client-side image resize before R2 upload.
 *
 * Anthropic downscales images > 1568px on the longest side server-side anyway
 * (and bills tokens proportional to resolution), so resizing on-device saves
 * bandwidth, R2 storage, and API tokens. An iPhone photo is 4032x3024; that's
 * roughly a 10x reduction in data for the same effective vision quality.
 *
 * Canvas-based — works on every browser we support. The object-URL revoke
 * calls matter: without them, every resized image leaks its source URL and
 * iOS Safari kills the PWA after a few multi-image turns.
 */

const MAX_DIMENSION = 1568;
const JPEG_QUALITY = 0.85;

export interface ResizeResult {
  blob: Blob;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  wasResized: boolean;
}

export async function resizeImageForUpload(file: File): Promise<ResizeResult> {
  const img = await loadImage(file);
  const origW = img.width;
  const origH = img.height;

  if (origW <= MAX_DIMENSION && origH <= MAX_DIMENSION) {
    return {
      blob: file,
      width: origW,
      height: origH,
      originalWidth: origW,
      originalHeight: origH,
      wasResized: false,
    };
  }

  const scale = MAX_DIMENSION / Math.max(origW, origH);
  const newW = Math.round(origW * scale);
  const newH = Math.round(origH * scale);

  const canvas = document.createElement('canvas');
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.drawImage(img, 0, 0, newW, newH);

  // JPEG for photos (much smaller), preserve PNG for screenshots / alpha.
  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const quality = outputType === 'image/jpeg' ? JPEG_QUALITY : undefined;

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      outputType,
      quality,
    );
  });

  return {
    blob,
    width: newW,
    height: newH,
    originalWidth: origW,
    originalHeight: origH,
    wasResized: true,
  };
}

/**
 * Small data-URL thumbnail for the preview strip — shown immediately, before
 * resize + upload complete. Kept separate from resizeImageForUpload so the
 * UI can render the thumb while the main resize is still running.
 */
export async function createThumbnail(file: File, maxSize = 200): Promise<string> {
  const img = await loadImage(file);
  const scale = maxSize / Math.max(img.width, img.height);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.drawImage(img, 0, 0, w, h);

  return canvas.toDataURL('image/jpeg', 0.6);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}
